import React, { forwardRef, useImperativeHandle, useState } from "react";
import Form from "react-bootstrap/Form";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import "../FormStyle.scss";
import { FormControlProps } from "react-bootstrap";
import { propsFilter } from "../../../app/tools";

interface TextWithValidationProps extends FormControlProps {
  autoFocus?: boolean;
  id: string;
  label: string;
  required?: boolean;
  initialValue?: any;
  disabled?: boolean;
  type?: string;
  validationFunction?: (input: string) => boolean;
  onChangeText?: (value: string) => void;
  validationResult?: (input: string, result: boolean) => void;
  formatFunction?: (input: string) => string;
}

export const TextWithValidation = forwardRef(
  (props: TextWithValidationProps, ref) => {
    const {
      initialValue,
      id,
      label,
      disabled,
      required,
      validationFunction,
      onChangeText,
      validationResult,
      type,
      formatFunction,
    } = props;

    const startValue = (): string => {
      if (initialValue === undefined) return "";
      const asString = initialValue?.toString() ?? "";
      if (!formatFunction) return asString;
      return formatFunction(asString);
    };

    const [value, setValue] = useState(startValue());
    const [isValid, setIsValid] = useState<boolean | undefined>(true);
    const [validationMessage, setValidationMessage] = useState("");

    React.useEffect(() => {
      handleValidation();
    }, [value]);

    function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
      let changedValue = event.target.value ?? "";
      setValue(changedValue);
      if (onChangeText) onChangeText(changedValue);
    }

    function handleBlur(event: React.FocusEvent<HTMLInputElement>) {
      let changedValue = event.target.value ?? "";
      setValue(changedValue);
    }

    function handleValidation(): boolean {
      let message = `Invalid ${label}`;
      let result = false;
      let valid = required ? value !== "" : true;

      if (required && value === "")
        message = "Field is required. It cannot be empty";
      else result = valid;

      setValidationMessage(message);

      if (validationFunction) result = validationFunction(value);
      if (validationResult) validationResult(value, result);

      setIsValid(result);

      if (formatFunction) {
        const formattedValue = formatFunction(value);
        if (formattedValue !== value) {
          setValue(formattedValue);
        }
      }

      console.debug(`Valid (${result}): ${label}. ${value}`);
      return result;
    }

    const filteredProps = propsFilter(props, [
      "id",
      "label",
      "required",
      "initialValue",
      "disabled",
      "type",
      "validationFunction",
      "onChangeText",
      "validationResult",
      "formatFunction",
    ]);

    useImperativeHandle(ref, () => ({
      validate(): boolean {
        console.debug(`Validating: ${label}.`);
        return handleValidation();
      },
    }));

    return (
      <Form.Group controlId={id}>
        <FloatingLabel label={label} className="mb-3">
          <Form.Control
            autoFocus={props.autoFocus}
            type={type}
            placeholder={label}
            required={required}
            value={value}
            onChange={handleInputChange}
            onBlur={handleBlur}
            isInvalid={isValid === undefined || !isValid}
            isValid={isValid}
            disabled={disabled ?? false}
            {...filteredProps}
          />
          <Form.Control.Feedback type="invalid">
            {validationMessage}.
          </Form.Control.Feedback>
        </FloatingLabel>
      </Form.Group>
    );
  }
);

TextWithValidation.defaultProps = {
  required: false,
};

export default TextWithValidation;

